import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { App as AntdApp } from "antd";
import AnalyticsTopBar from "features/analytics/studio/components/shell/AnalyticsTopBar";
import { useBreadcrumb } from "shared/context/BreadcrumbContext";
import axiosInstance from "shared/services/api/axiosInstance";
import AddToBoardDropdown from "features/analytics/studio/components/boards/AddToBoardDropdown";
import HomeChatGateway from "features/dashboard/components/HomeChatGateway";
import AddContentBlock from "features/analytics/studio/components/boards/AddContentBlock";
import ConfirmModal from "features/analytics/studio/components/boards/ConfirmModal";
import BoardCanvas from "features/analytics/studio/components/board/BoardCanvas";
import { trodoAnalyticsTheme as baseTheme } from "features/analytics/studio/theme";
import { useBoard } from "features/analytics/studio/hooks/useBoards";
import { useCards, useHomeWorkspaceCanvas } from "features/analytics/studio/hooks/useInsights";
import * as analyticsApi from "features/analytics/studio/api/analyticsApi";
import { Plus, Loader2, Pencil, Check, ChevronLeft, ChevronRight, Clock, X } from "lucide-react";
import EventsTimeFilter from "features/activity/components/EventsTimeFilter";
import {
  toRGLLayout,
  fromRGLLayout,
  defaultGridPosition,
} from "features/analytics/studio/utils/boardLayoutUtils";

const theme = baseTheme;

/** Dotted canvas background — uses border-default for dots so it adapts to dark/light mode */
const BOARD_BG_STYLE = {
  backgroundColor: "var(--surface-base)",
  backgroundImage: "radial-gradient(circle, var(--border-default) 1px, transparent 1px)",
  backgroundSize: "20px 20px",
};

// ─── Permission helpers ────────────────────────────────────────────────────

const GRANTED_ROLE_OPTIONS = [
  { value: "viewer", label: "Can view" },
  { value: "editor", label: "Can edit" },
];
const OWNER_OPTION = [{ value: "owner", label: "Owner" }];

function getTeamMemberUserIdInt(mem) {
  const raw = mem?.userId ?? mem?.user_id ?? mem?.id;
  if (raw == null) return null;
  const n = typeof raw === "number" ? raw : parseInt(String(raw).trim(), 10);
  return Number.isFinite(n) ? n : null;
}

function getPermissionsObject(board) {
  const raw = board?.permissions;
  if (raw == null) return null;
  if (typeof raw === "string") {
    const s = raw.trim();
    if (s === "" || s === "{}") return {};
    try { const o = JSON.parse(s); return typeof o === "object" && o !== null && !Array.isArray(o) ? o : null; }
    catch { return null; }
  }
  if (typeof raw === "object" && !Array.isArray(raw)) return raw;
  return null;
}

function getStoredUserId() {
  try {
    const raw = localStorage.getItem("User");
    if (!raw) return null;
    const u = JSON.parse(raw);
    const id = u.id ?? u._id;
    const n = Number(id);
    return Number.isFinite(n) ? n : null;
  } catch { return null; }
}

function rowIsLegacyPermissions(board) {
  const raw = board?.permissions;
  if (raw == null) return true;
  const obj = getPermissionsObject(board);
  if (obj === null) return true;
  return Object.keys(obj).length === 0;
}

function getBoardPermissions(board) {
  const obj = getPermissionsObject(board);
  const p = obj && typeof obj === "object" ? obj : {};
  const editors = Array.isArray(p.editors) ? p.editors.map(Number).filter(Number.isFinite) : [];
  const viewers = Array.isArray(p.viewers) ? p.viewers.map(Number).filter(Number.isFinite) : [];
  const owner = Number(p.owner ?? board?.userId);
  return { editors, viewers, owner: Number.isFinite(owner) ? owner : null };
}

function isBoardOwner(board, userId) {
  if (userId == null || !board) return false;
  const uid = Number(userId);
  if (!Number.isFinite(uid)) return false;
  const creator = Number(board.userId);
  if (rowIsLegacyPermissions(board)) return uid === creator;
  const { owner } = getBoardPermissions(board);
  const ownerId = Number.isFinite(owner) ? owner : creator;
  return uid === creator || uid === ownerId;
}

function canEditBoard(board, userId) {
  if (!board || userId == null) return false;
  const uid = Number(userId);
  if (!Number.isFinite(uid)) return false;
  if (rowIsLegacyPermissions(board)) return isBoardOwner(board, userId);
  if (isBoardOwner(board, userId)) return true;
  const { editors } = getBoardPermissions(board);
  return editors.includes(uid);
}

function buildGrantedAccessDraft(board) {
  const draft = {};
  if (!board || rowIsLegacyPermissions(board)) return draft;
  const { editors, viewers } = getBoardPermissions(board);
  editors.forEach((id) => { if (Number.isFinite(id)) draft[String(id)] = "editor"; });
  viewers.forEach((id) => { if (Number.isFinite(id)) draft[String(id)] = "viewer"; });
  return draft;
}

function sortNumericIdsByMemberName(ids, teamMembers) {
  return [...ids].sort((a, b) => {
    const na = teamMembers.find((m) => getTeamMemberUserIdInt(m) === a);
    const nb = teamMembers.find((m) => getTeamMemberUserIdInt(m) === b);
    return String(na?.name || na?.email || `User ${a}`).localeCompare(String(nb?.name || nb?.email || `User ${b}`));
  });
}

const TEAM_WIDGET_TITLE_BY_VARIANT = {
  team_pulse: "Team pulse",
  agent_operations: "Agent operations",
  web_activity: "Web activity",
  product_events: "Product events",
  session_engagement: "Session engagement",
  user_momentum: "User momentum",
  quality_feedback: "Quality & feedback",
  reliability_errors: "Reliability & errors",
  tool_performance: "Tool performance",
  workspace_health: "Workspace health",
};

const SAVED_INDICATOR_MS = 2000;
const INITIAL_BOARD_TIME = { mode: "default" };

// ─── Main page ──────────────────────────────────────────────────────────────

export default function BoardViewPage({ variant = "board" }) {
  // Context-aware, unlike the static `message` import: antd v5 renders static
  // calls through a detached root that never sees ConfigProvider, so those
  // toasts stayed light in dark mode. Shadows nothing — the other `message`
  // identifiers here are confirmDialog's own property.
  const { message } = AntdApp.useApp();
  const isHomeMain = variant === "home";
  const isHomeCustomize = variant === "homeCustomize";
  const isHome = isHomeMain || isHomeCustomize;
  const { boardId, team } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedTeamData = JSON.parse(localStorage.getItem("selectedTeamData") || "{}");
  const teamId = selectedTeamData.id ?? selectedTeamData._id ?? null;

  const { board, loading: boardLoading, error: boardError, loadBoard, updateBoard } = useBoard(boardId, teamId);
  const [boardTime, setBoardTime] = useState(INITIAL_BOARD_TIME);
  const homeCanvas = useHomeWorkspaceCanvas(teamId, INITIAL_BOARD_TIME, isHome);
  const boardCanvas = useCards(boardId, teamId, boardTime);
  const cards        = isHome ? homeCanvas.cards        : boardCanvas.cards;
  const cardResults  = isHome ? homeCanvas.cardResults  : boardCanvas.cardResults;
  const cardsLoading = isHome ? homeCanvas.loading      : boardCanvas.loading;
  const deleteCard   = isHome ? homeCanvas.deleteCard   : boardCanvas.deleteCard;
  const executeAllCards = isHome ? homeCanvas.executeAllCards : boardCanvas.executeAllCards;
  const createCard   = isHome ? homeCanvas.createCard   : boardCanvas.createCard;
  const loadCards    = isHome ? homeCanvas.loadCards    : boardCanvas.loadCards;
  const updateCard   = isHome ? homeCanvas.updateCard   : boardCanvas.updateCard;
  const homeWorkspace = homeCanvas.workspace;
  const homeSetupLoading = homeCanvas.setupLoading;
  const homeWorkspaceError = homeCanvas.error;

  const [editingTitle, setEditingTitle] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [titleValue, setTitleValue] = useState("");
  const [descriptionValue, setDescriptionValue] = useState("");
  const [boardIcon, setBoardIcon] = useState(null);
  const [saveStatus, setSaveStatus] = useState("idle");
  const [reorderSaving] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});
  const savedTimeoutRef = useRef(null);
  const skipGridSyncRef = useRef(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: "", message: "", confirmLabel: "Delete", cancelLabel: "Cancel", danger: true, onConfirm: null });
  const [gridLayout, setGridLayout] = useState([]);
  const layoutSaveTimerRef = useRef(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [publicUrl, setPublicUrl] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [grantedAccess, setGrantedAccess] = useState({});
  const [shareAddUserId, setShareAddUserId] = useState(null);
  const [shareAddRole, setShareAddRole] = useState("viewer");
  const [permissionsSaving, setPermissionsSaving] = useState(false);

  const currentUserId = useMemo(() => getStoredUserId(), []);
  const boardReadOnly = useMemo(() => {
    if (isHomeMain) return true;
    if (isHomeCustomize) return false;
    return !!(board && !canEditBoard(board, currentUserId));
  }, [isHomeMain, isHomeCustomize, board, currentUserId]);
  const isBoardOwnerUser = useMemo(() => {
    if (isHome) return true;
    return !!(board && isBoardOwner(board, currentUserId));
  }, [isHome, board, currentUserId]);
  const canManagePublicLink = useMemo(() => {
    if (isHome) return false;
    return !!(board && canEditBoard(board, currentUserId));
  }, [isHome, board, currentUserId]);

  const permissionsDirty = useMemo(() => {
    if (isHome || !board) return false;
    const { editors, viewers, owner } = getBoardPermissions(board);
    const creatorId = Number(board.userId);
    const ownerId = Number.isFinite(owner) ? owner : creatorId;
    const dEd = []; const dVi = [];
    Object.entries(grantedAccess).forEach(([k, r]) => {
      const id = parseInt(k, 10);
      if (!Number.isFinite(id) || id === ownerId) return;
      if (r === "editor") dEd.push(id);
      if (r === "viewer") dVi.push(id);
    });
    const s = (a) => [...a].sort((x, y) => x - y);
    return JSON.stringify({ e: s(editors), v: s(viewers) }) !== JSON.stringify({ e: s(dEd), v: s(dVi) });
  }, [isHome, board, grantedAccess]);

  const shareOwnerId = useMemo(() => {
    if (!board) return null;
    const { owner } = getBoardPermissions(board);
    const creator = Number(board.userId);
    return Number.isFinite(owner) ? owner : Number.isFinite(creator) ? creator : null;
  }, [board]);

  const grantedUserIdsSorted = useMemo(() => {
    const ids = Object.keys(grantedAccess).map((k) => parseInt(k, 10)).filter(Number.isFinite);
    return sortNumericIdsByMemberName(ids, teamMembers);
  }, [grantedAccess, teamMembers]);

  const shareAddMemberOptions = useMemo(() => {
    if (shareOwnerId == null) return [];
    return teamMembers.map((m) => {
      const id = getTeamMemberUserIdInt(m);
      if (id == null || id === shareOwnerId || grantedAccess[String(id)]) return null;
      const label = [m.name || "Member", m.email].filter(Boolean).join(" · ");
      return { value: String(id), label: label || `User ${id}` };
    }).filter(Boolean);
  }, [teamMembers, shareOwnerId, grantedAccess]);

  useEffect(() => {
    if (isHome) { setTitleValue("My workspace"); setDescriptionValue(""); setBoardIcon(null); return; }
    if (board) {
      setTitleValue(board.name || "Untitled");
      setDescriptionValue(board.description || "");
      setBoardIcon(board.icon || null);
    }
  }, [board, isHome]);

  useEffect(() => {
    if (!showShareModal || !boardId || !teamId || !board) return;
    let cancelled = false;
    (async () => {
      setShareLoading(true);
      try {
        const r = await analyticsApi.getBoardShareStatus(boardId, teamId);
        if (!cancelled) setPublicUrl(r?.data?.isPublic && r?.data?.publicUrl ? r.data.publicUrl : null);
      } catch { if (!cancelled) setPublicUrl(null); }
      if (isBoardOwner(board, currentUserId)) {
        try {
          const m = await axiosInstance.get(`/team/${teamId}/members`);
          if (!cancelled) {
            setTeamMembers(m.data?.members || []);
            setGrantedAccess(buildGrantedAccessDraft(board));
            setShareAddUserId(null); setShareAddRole("viewer");
          }
        } catch { if (!cancelled) { setTeamMembers([]); setGrantedAccess({}); } }
      } else if (!cancelled) { setTeamMembers([]); setGrantedAccess({}); }
      if (!cancelled) setShareLoading(false);
    })();
    return () => { cancelled = true; };
  }, [showShareModal, boardId, teamId, board, currentUserId]);

  const gridCards = useMemo(() => {
    return (cards || []).map((card) => {
      const pending = pendingChanges[card.id];
      if (!pending?.queryDsl) return card;
      const baseQ = (card.queryDsl && typeof card.queryDsl === "object" ? card.queryDsl : {}) || {};
      return { ...card, queryDsl: { ...baseQ, ...pending.queryDsl } };
    });
  }, [cards, pendingChanges]);

  useEffect(() => {
    if (skipGridSyncRef.current) { skipGridSyncRef.current = false; return; }
    setGridLayout(toRGLLayout(gridCards));
  }, [gridCards.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const workingCards = gridCards;

  // Deep-link scroll
  const focusCardIdFromUrl = searchParams.get("cardId");
  useEffect(() => {
    if (!focusCardIdFromUrl || isHome || cardsLoading) return;
    // Cards are now in grid so we scroll the page to the card by its DOM id
    requestAnimationFrame(() => {
      const el = document.getElementById(`board-card-${focusCardIdFromUrl}`);
      if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [focusCardIdFromUrl, isHome, cardsLoading, gridCards]);

  const isDirty = useMemo(() => Object.keys(pendingChanges).length > 0, [pendingChanges]);

  useEffect(() => () => { if (savedTimeoutRef.current) clearTimeout(savedTimeoutRef.current); }, []);
  useEffect(() => {
    if (cards.length > 0 && Object.keys(cardResults).length === 0) executeAllCards();
  }, [cards, cardResults, executeAllCards]);

  const breadcrumbs = ["Reports", titleValue || board?.name || "Untitled"];

  // Register path in GlobalTopBar: Reports / Board name
  useBreadcrumb([
    { label: "Reports", to: `/${team}/analytics/reports` },
    { label: titleValue || board?.name || "Untitled" },
  ]);

  const showSavedThenIdle = useCallback(() => {
    setSaveStatus("saved");
    if (savedTimeoutRef.current) clearTimeout(savedTimeoutRef.current);
    savedTimeoutRef.current = setTimeout(() => { setSaveStatus("idle"); savedTimeoutRef.current = null; }, SAVED_INDICATOR_MS);
  }, []);

  const handleAddToBoardSelect = useCallback((id) => {
    const bid = boardId || "new";
    const boardEditorPath = (suffix) => isHome ? `/${team}/home/editor${suffix}` : `/${team}/analytics/boards/${bid}/editor${suffix}`;
    const flowPath = isHome ? `/${team}/home/flow` : `/${team}/analytics/flow/${bid}`;
    const pos = defaultGridPosition(id, gridLayout);
    const posParams = `x=${pos.x}&y=${pos.y}&w=${pos.w}&h=${pos.h}`;
    const createSimpleCard = (cardType, title, queryDsl, extra = {}) => {
      createCard({ cardType, title, queryDsl, visualization: {}, position: pos, ...extra })
        .then(() => loadCards())
        .catch((err) => console.error(`Failed to create ${cardType} card:`, err));
    };
    switch (id) {
      case "insights":  navigate(boardEditorPath(`?type=insights&${posParams}`)); return;
      case "funnels":   navigate(boardEditorPath(`?type=funnels&${posParams}`)); return;
      case "flows":     navigate(`${flowPath}?${posParams}`); return;
      case "retention": navigate(boardEditorPath(`?type=retention&${posParams}`)); return;
      case "text":  createSimpleCard("text", "Text", { content: "" }); return;
      case "media": createSimpleCard("media", "Media", { url: "", type: "image" }); return;
      case "quick_links":
        if (!isHome) break;
        createSimpleCard("quick_links", "Reports & cards", { items: [] }); return;
      default:
        if (typeof id === "string" && id.startsWith("team_widget:") && isHome) {
          const wVariant = id.slice("team_widget:".length);
          createSimpleCard("team_widget", TEAM_WIDGET_TITLE_BY_VARIANT[wVariant] || "Team widget", null, { visualization: { variant: wVariant } });
        }
    }
  }, [boardId, team, navigate, createCard, loadCards, gridLayout, isHome]);

  const handleDeleteCard = (cardId) => {
    setConfirmDialog({
      open: true, title: "Delete chart",
      message: "Are you sure you want to delete this chart? This cannot be undone.",
      confirmLabel: "Delete", cancelLabel: "Cancel", danger: true,
      onConfirm: async () => {
        setConfirmDialog((d) => ({ ...d, open: false }));
        try {
          await deleteCard(cardId);
          setGridLayout((prev) => prev.filter((item) => item.i !== String(cardId)));
          setPendingChanges((prev) => { const n = { ...prev }; delete n[cardId]; return n; });
          await loadCards();
        } catch (err) { console.error("Failed to delete card:", err); }
      },
    });
  };

  const handleLayoutChange = useCallback((newLayout) => {
    setGridLayout(newLayout);
    // Always clear any pending save — we'll re-schedule if appropriate.
    clearTimeout(layoutSaveTimerRef.current);
    layoutSaveTimerRef.current = setTimeout(async () => {
      if (boardReadOnly) return;
      const positions = fromRGLLayout(newLayout);
      if (positions.length === 0) return;
      try {
        if (isHome) await analyticsApi.updateHomeWorkspaceCardPositions(positions, teamId);
        else await analyticsApi.updateCardPositions(boardId, positions, teamId);
      } catch (err) { console.error("Failed to auto-save card positions:", err); }
    }, 1200);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardReadOnly, isHome, boardId, teamId]);

  const editorTypeFromCardType = (cardType) => {
    const t = (cardType || "insights").toLowerCase();
    if (t === "funnel") return "funnels";
    if (t === "flow") return "flows";
    if (t === "retention") return "retention";
    return t;
  };

  const handleEditCard = (card) => {
    const rawType = (card?.cardType || "").toLowerCase();
    if (rawType === "quick_links" || rawType === "team_widget") return;
    const type = editorTypeFromCardType(card?.cardType);
    if (isHome) {
      navigate(type === "flows" ? `/${team}/home/flow?cardId=${card?.id || ""}` : `/${team}/home/editor?type=${type}&cardId=${card?.id || ""}`);
      return;
    }
    navigate(type === "flows" ? `/${team}/analytics/flow/${boardId}?cardId=${card?.id || ""}` : `/${team}/analytics/boards/${boardId}/editor?type=${type}&cardId=${card?.id || ""}`);
  };

  const handleSaveTextContent = useCallback((cardId, payload) => {
    const working = workingCards.find((c) => c.id === cardId);
    const existing = (working?.queryDsl && typeof working.queryDsl === "object" ? working.queryDsl : {}) || {};
    const nextQ = typeof payload === "string"
      ? { ...existing, content: payload || "" }
      : payload && typeof payload === "object"
        ? { ...existing, title: payload.title ?? "", content: payload.content ?? "", textStyle: payload.textStyle ?? "text" }
        : existing;
    setPendingChanges((prev) => ({ ...prev, [cardId]: { ...prev[cardId], queryDsl: nextQ } }));
  }, [workingCards]);

  const handleSaveMediaUrl = useCallback((cardId, url, type) => {
    const working = workingCards.find((c) => c.id === cardId);
    const existing = (working?.queryDsl && typeof working.queryDsl === "object" ? working.queryDsl : {}) || {};
    setPendingChanges((prev) => ({ ...prev, [cardId]: { ...prev[cardId], queryDsl: { ...existing, url: url || "", type: type || "image" } } }));
  }, [workingCards]);

  const handleSaveMediaDisplayOptions = useCallback((cardId, payload) => {
    const working = workingCards.find((c) => c.id === cardId);
    const existing = (working?.queryDsl && typeof working.queryDsl === "object" ? working.queryDsl : {}) || {};
    setPendingChanges((prev) => ({ ...prev, [cardId]: { ...prev[cardId], queryDsl: { ...existing, ...payload } } }));
  }, [workingCards]);

  const handleGlobalSave = useCallback(async () => {
    if (boardReadOnly || (!isHome && !boardId) || !isDirty) return;
    setSaveStatus("saving");
    try {
      for (const [cardId, pending] of Object.entries(pendingChanges)) {
        const baseCard = cards.find((c) => c.id === cardId);
        const baseQ = (baseCard?.queryDsl && typeof baseCard.queryDsl === "object" ? baseCard.queryDsl : {}) || {};
        const updates = {};
        if (pending.queryDsl && Object.keys(pending.queryDsl).length > 0) updates.queryDsl = { ...baseQ, ...pending.queryDsl };
        if (Object.keys(updates).length > 0) await updateCard(cardId, updates);
      }
      setPendingChanges({});
      await loadCards();
      showSavedThenIdle();
    } catch (err) { console.error("Failed to save board changes:", err); setSaveStatus("idle"); }
  }, [boardId, isHome, isDirty, pendingChanges, cards, updateCard, loadCards, showSavedThenIdle, boardReadOnly]);

  const handleShareBoard = useCallback(() => {
    if (!teamId) { message.error("Team ID not found."); return; }
    if (!boardId) return;
    setUrlCopied(false); setShowShareModal(true);
  }, [boardId, teamId, message]);

  const handleCreatePublicLink = useCallback(async () => {
    if (!teamId || !boardId) return;
    setShareLoading(true);
    try {
      const r = await analyticsApi.createBoardShare(boardId, teamId);
      if (r?.data?.publicUrl) { setPublicUrl(r.data.publicUrl); message.success("Public link created"); }
    } catch (err) { message.error(analyticsApi.getErrorMessage(err) || "Failed to create public link"); }
    finally { setShareLoading(false); }
  }, [boardId, teamId, message]);

  const handleGrantedRoleChange = useCallback((uid, val) => setGrantedAccess((prev) => ({ ...prev, [uid]: val })), []);
  const handleRemoveGranted = useCallback((uid) => setGrantedAccess((prev) => { const n = { ...prev }; delete n[uid]; return n; }), []);
  const handleAddGrantedMember = useCallback(() => {
    if (!shareAddUserId) return;
    setGrantedAccess((prev) => ({ ...prev, [String(shareAddUserId)]: shareAddRole }));
    setShareAddUserId(null);
  }, [shareAddUserId, shareAddRole]);

  const handleSaveBoardPermissions = useCallback(async () => {
    if (!board || !isBoardOwner(board, currentUserId)) return;
    const { owner: permOwner } = getBoardPermissions(board);
    const ownerId = Number.isFinite(permOwner) ? permOwner : Number(board.userId);
    const editors = []; const viewers = [];
    Object.entries(grantedAccess).forEach(([k, r]) => {
      const uid = parseInt(k, 10);
      if (!Number.isFinite(uid) || uid === ownerId) return;
      if (r === "editor") editors.push(uid); else if (r === "viewer") viewers.push(uid);
    });
    setPermissionsSaving(true);
    try { await updateBoard({ permissions: { owner: ownerId, editors, viewers } }, { silent: true }); message.success("Team access updated"); }
    catch (err) { message.error(analyticsApi.getErrorMessage(err) || "Failed to update access"); }
    finally { setPermissionsSaving(false); }
  }, [board, currentUserId, grantedAccess, updateBoard, message]);

  const copyPublicUrl = useCallback(async () => {
    if (!publicUrl) return;
    try { await navigator.clipboard.writeText(publicUrl); setUrlCopied(true); message.success("Copied!"); setTimeout(() => setUrlCopied(false), 2000); }
    catch { message.error("Failed to copy URL"); }
  }, [publicUrl, message]);

  const revokeBoardShare = useCallback(async () => {
    if (!teamId || !boardId) return;
    setShareLoading(true);
    try { await analyticsApi.revokeBoardShare(boardId, teamId); setPublicUrl(null); message.success("Public link removed"); }
    catch { message.error("Failed to revoke public access"); }
    finally { setShareLoading(false); }
  }, [boardId, teamId, message]);

  const handleRenameBoard = useCallback(() => setEditingTitle(true), []);

  const handleIconChange = useCallback((icon) => {
    if (boardReadOnly) return;
    // Optimistic local update — the icon is persisted when the user clicks Save
    // (handleSaveBoard includes icon in payload when changed). For an extra
    // seamless feel we also fire a silent background save immediately.
    setBoardIcon(icon !== undefined ? icon : null);
  }, [boardReadOnly]);

  const handleDeleteBoard = useCallback(() => {
    if (!boardId) return;
    setConfirmDialog({
      open: true, title: "Delete board",
      message: "Are you sure you want to delete this board? This cannot be undone.",
      confirmLabel: "Delete", cancelLabel: "Cancel", danger: true,
      onConfirm: async () => {
        setConfirmDialog((d) => ({ ...d, open: false }));
        try { await analyticsApi.deleteBoard(boardId); navigate(`/${team}/analytics/reports`); }
        catch (err) { console.error("Failed to delete board:", err); }
      },
    });
  }, [boardId, team, navigate]);

  const handleSaveBoard = useCallback(async () => {
    if (boardReadOnly || !board) return;
    const name = (titleValue || "").trim() || "Untitled";
    const description = (descriptionValue || "").trim() || null;
    const nameChanged = name !== (board.name || "Untitled");
    const descChanged = (description ?? "") !== (board.description ?? "");
    const iconChanged = boardIcon !== (board.icon || null);
    if (!nameChanged && !descChanged && !iconChanged) return;
    setSaveStatus("saving");
    const payload = { name, description: description ?? "" };
    if (iconChanged) payload.icon = boardIcon ?? null;
    try { await updateBoard(payload, { silent: true }); showSavedThenIdle(); }
    catch { setSaveStatus("idle"); }
  }, [boardReadOnly, board, titleValue, descriptionValue, boardIcon, updateBoard, showSavedThenIdle]);

  const handleSaveClick = useCallback(async () => {
    if (isDirty) await handleGlobalSave();
    await handleSaveBoard();
  }, [isDirty, handleGlobalSave, handleSaveBoard]);

  const handleSaveCustomizeAndReturn = useCallback(async () => {
    if (!isHomeCustomize) return;
    if (isDirty) await handleGlobalSave();
    if (team) navigate(`/${team}/home`);
  }, [isHomeCustomize, isDirty, handleGlobalSave, team, navigate]);

  const handleTitleBlur = () => {
    setEditingTitle(false);
    setTitleValue((v) => (v || "").trim() || "Untitled");
  };

  const boardMetaDirty = !isHome && !!board &&
    (((titleValue || "").trim() || "Untitled") !== (board.name || "Untitled") ||
      (descriptionValue ?? "").trim() !== (board.description ?? "") ||
      boardIcon !== (board.icon || null));
  const hasUnsavedChanges = isDirty || boardMetaDirty;

  useEffect(() => {
    const onBeforeUnload = (e) => { if (isDirty) e.preventDefault(); };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  const layoutRootRef = useRef(null);
  const boardScrollRef = useRef(null);

  // ── Loading / error states ──────────────────────────────────────────────

  if (isHome && homeSetupLoading) {
    return (
      <div className="flex flex-col flex-1 min-h-0" style={BOARD_BG_STYLE}>
        <main className="flex-1 overflow-auto p-6 flex items-center justify-center">
          <div className="text-[13px] text-center text-[color:var(--text-muted)]">
            <div className="font-medium mb-1 text-[color:var(--text-primary)]">Setting up your workspace</div>
            Loading charts and layout…
          </div>
        </main>
      </div>
    );
  }

  if (!isHome && boardLoading) {
    return (
      <div className="flex flex-col flex-1 min-h-0" style={{ backgroundColor: "var(--surface-base)" }}>
        <AnalyticsTopBar breadcrumbs={breadcrumbs} showCreateNew={false} showTimeRange={false} />
        <main className="flex-1 overflow-auto p-6 flex items-center justify-center" style={BOARD_BG_STYLE}>
          <div className="text-[13px] text-[color:var(--text-muted)]">Loading board…</div>
        </main>
      </div>
    );
  }

  if (isHome && homeWorkspaceError) {
    return (
      <div className="flex flex-col flex-1 min-h-0" style={BOARD_BG_STYLE}>
        <main className="flex-1 overflow-auto p-6 flex items-center justify-center">
          <div className="text-sm" style={{ color: "var(--feedback-error)" }}>Error: {homeWorkspaceError}</div>
        </main>
      </div>
    );
  }

  if (!isHome && boardError) {
    return (
      <div className="flex flex-col flex-1 min-h-0" style={{ backgroundColor: "var(--surface-base)" }}>
        <AnalyticsTopBar breadcrumbs={breadcrumbs} showCreateNew={false} showTimeRange={false} />
        <main className="flex-1 overflow-auto p-6 flex items-center justify-center" style={BOARD_BG_STYLE}>
          <div className="text-sm" style={{ color: "var(--feedback-error)" }}>Error: {boardError}</div>
        </main>
      </div>
    );
  }

  // ── Main render ─────────────────────────────────────────────────────────

  return (
    <div
      ref={layoutRootRef}
      className={isHome ? "flex flex-col w-full min-h-min" : "flex flex-col flex-1 min-h-0"}
      style={{ backgroundColor: "var(--surface-base)" }}
    >
      <ConfirmModal
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel={confirmDialog.confirmLabel}
        cancelLabel={confirmDialog.cancelLabel}
        danger={confirmDialog.danger}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog((d) => ({ ...d, open: false }))}
      />

      {/* ── Home customize header ── */}
      {isHomeCustomize ? (
        <header
          className="shrink-0 w-full border-b border-[color:var(--border-default)] bg-surface px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-3"
        >
          <div className="min-w-0">
            <h1 className="text-base sm:text-lg font-semibold text-[color:var(--text-primary)]">Customize</h1>
            <p className="text-[12px] mt-0.5 text-[color:var(--text-muted)]">Add widgets and arrange your workspace: save when you are done</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button type="button" onClick={() => team && navigate(`/${team}/home`)} className="h-8 rounded-md px-3 text-[12px] font-medium border border-[color:var(--border-default)] bg-surface text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover">Cancel</button>
            <button type="button" onClick={() => void handleSaveCustomizeAndReturn()} disabled={saveStatus === "saving"} className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium text-ink-onBrand bg-[color:var(--trodo-primary)] hover:opacity-95 disabled:opacity-60">
              {saveStatus === "saving" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Save &amp; return home
            </button>
          </div>
        </header>
      ) : null}

      {/* ── Board top bar ── */}
      {!isHome ? (
        <AnalyticsTopBar
          breadcrumbs={breadcrumbs}
          showCreateNew={false}
          showTimeRange={false}
          showSaveClose
          saveOnly
          onSave={handleSaveClick}
          saveStatus={saveStatus}
          reorderSaving={reorderSaving}
          saveDisabled={!hasUnsavedChanges}
          showBoardActions
          onShare={handleShareBoard}
          onDelete={handleDeleteBoard}
          onRename={handleRenameBoard}
          boardReadOnly={boardReadOnly}
          timeSlot={<BoardTimeButton boardTime={boardTime} onBoardTimeChange={setBoardTime} />}
          leftSlot={
            <div className="flex min-w-0 items-center gap-2">
              {/* Compact icon picker */}
              <BoardIconPicker
                icon={boardIcon}
                onIconChange={handleIconChange}
                readOnly={boardReadOnly}
                compact
              />

              {/* Title + optional description stacked */}
              <div className="flex min-w-0 flex-col justify-center">
                {editingTitle && !boardReadOnly ? (
                  <input
                    type="text"
                    value={titleValue}
                    onChange={(e) => setTitleValue(e.target.value)}
                    onBlur={handleTitleBlur}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleTitleBlur();
                      if (e.key === "Escape") { setTitleValue(board?.name || "Untitled"); setEditingTitle(false); }
                    }}
                    autoFocus
                    className="min-w-[8rem] max-w-[min(24rem,36vw)] border-0 border-b bg-transparent py-0 text-[13px] font-semibold outline-none"
                    style={{ color: "var(--text-primary)", borderColor: "var(--brand-primary)", fontFamily: "Inter, sans-serif" }}
                    aria-label="Board name"
                  />
                ) : (
                  <span
                    className={`max-w-[min(24rem,36vw)] truncate text-[13px] font-semibold leading-tight text-[color:var(--text-primary)] ${boardReadOnly ? "" : "cursor-pointer hover:opacity-75 transition-opacity"}`}
                    onClick={() => { if (!boardReadOnly) setEditingTitle(true); }}
                    title={boardReadOnly ? undefined : "Click to rename"}
                  >
                    {titleValue || board?.name || "Untitled"}
                  </span>
                )}

                {/* Description: only shown when it has content or is being edited */}
                {(descriptionValue || editingDescription) ? (
                  editingDescription && !boardReadOnly ? (
                    <textarea
                      value={descriptionValue}
                      onChange={(e) => setDescriptionValue(e.target.value)}
                      onBlur={() => { setEditingDescription(false); setDescriptionValue((v) => (v || "").trim() || ""); }}
                      onKeyDown={(e) => { if (e.key === "Escape") { setDescriptionValue(board?.description || ""); setEditingDescription(false); } }}
                      autoFocus
                      placeholder="Add a description…"
                      rows={1}
                      className="max-w-[min(28rem,40vw)] resize-none border-0 bg-transparent py-0 text-[11px] leading-tight outline-none"
                      style={{ color: "var(--text-muted)", fontFamily: "Inter, sans-serif" }}
                    />
                  ) : (
                    <span
                      className={`max-w-[min(28rem,40vw)] truncate text-[11px] leading-tight text-[color:var(--text-muted)] ${boardReadOnly ? "" : "cursor-pointer hover:opacity-75 transition-opacity"}`}
                      onClick={() => { if (!boardReadOnly) setEditingDescription(true); }}
                    >
                      {descriptionValue}
                    </span>
                  )
                ) : !boardReadOnly ? (
                  <span
                    className="cursor-pointer text-[11px] leading-tight text-[color:var(--text-muted)] opacity-0 transition-opacity hover:opacity-100"
                    onClick={() => setEditingDescription(true)}
                  >
                    Add description…
                  </span>
                ) : null}
              </div>
            </div>
          }
        />
      ) : null}

      {isHomeMain ? <HomeChatGateway /> : null}

      {/* ── Main scroll area ── */}
      <main
        ref={isHome ? undefined : boardScrollRef}
        className={isHome ? "relative min-w-0 w-full px-4 pt-2 pb-8 group/homeWidgets" : "flex-1 min-w-0 overflow-auto pb-8"}
        style={{ ...BOARD_BG_STYLE, fontFamily: "Inter, sans-serif" }}
      >
        {/* Home main: Customize button */}
        {isHomeMain ? (
          <div className="absolute top-3.5 right-4 z-20 flex items-center justify-end opacity-0 pointer-events-none transition-opacity duration-200 group-hover/homeWidgets:opacity-100 group-hover/homeWidgets:pointer-events-auto">
            <button
              type="button"
              onClick={() => team && navigate(`/${team}/home/customize`)}
              className="inline-flex h-8 items-center gap-2 rounded-md border border-[color:var(--border-default)] bg-surface px-3 text-[12px] font-medium text-[color:var(--text-primary)] shadow-token-sm transition-colors hover:bg-surface-hover"
            >
              <Pencil className="w-4 h-4" /> Customize
            </button>
          </div>
        ) : null}

        {isHomeMain ? (
          <div className="w-full min-h-[3rem] sm:min-h-[3.25rem] shrink-0 border-b border-[color:var(--border-default)] mb-6" />
        ) : null}

        <div className="w-full min-w-0 max-w-full">
          {/* ── Grid canvas via BoardCanvas ── */}
          <div className={isHome ? "" : "px-4 pt-3"}>
          <BoardCanvas
            cards={gridCards}
            layout={gridLayout}
            cardResults={cardResults}
            readOnly={boardReadOnly}
            onLayoutChange={handleLayoutChange}
            onEdit={handleEditCard}
            onDelete={handleDeleteCard}
            onSaveTextContent={handleSaveTextContent}
            onSaveMediaUrl={handleSaveMediaUrl}
            onSaveDisplayOptions={handleSaveMediaDisplayOptions}
            boardId={isHome ? undefined : boardId}
            homeWorkspaceId={isHome ? homeWorkspace?.id : undefined}
          />
          </div>

          {/* ── Add content block (editable boards only) ── */}
          {!boardReadOnly ? (
            <div
              className={isHome ? "" : "px-4"}
              style={{ paddingTop: gridCards.length > 0 ? 8 : 0, paddingBottom: 48 }}
            >
              <AddContentBlock variant="end" insertIndex={gridCards.length} onSelect={handleAddToBoardSelect} />
            </div>
          ) : null}
        </div>
      </main>

      {/* ── Share Dialog: custom portal, fully token-driven for both light and dark ── */}
      {showShareModal && typeof document !== "undefined" && createPortal(
        <div
          className="fixed inset-0 z-[99998] flex items-center justify-center p-4"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-surface-overlay"
            onClick={() => { setShowShareModal(false); setUrlCopied(false); setShareAddUserId(null); setShareAddRole("viewer"); }}
          />
          {/* Dialog */}
          <div
            className="relative flex flex-col w-full max-w-[560px] max-h-[90vh] overflow-hidden rounded-xl border border-[color:var(--border-default)] bg-surface shadow-token-lg"
          >
            {/* Header */}
            <div className="flex shrink-0 items-center justify-between border-b border-[color:var(--border-default)] px-6 py-4">
              <h2 className="text-[14px] font-semibold text-[color:var(--text-primary)]">Share Board</h2>
              <button
                type="button"
                onClick={() => { setShowShareModal(false); setUrlCopied(false); setShareAddUserId(null); setShareAddRole("viewer"); }}
                className="flex h-7 w-7 items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">

              {/* ── Team access ── */}
              <div>
                <div className="mb-2 text-[13px] font-semibold text-[color:var(--text-primary)]">Team access</div>
                {rowIsLegacyPermissions(board) && isBoardOwnerUser ? (
                  <p className="mb-3 text-[12px] text-[color:var(--text-muted)]">
                    The first time you save, you choose who can view or edit. Until then only you can access this board.
                  </p>
                ) : null}

                {isBoardOwnerUser ? (
                  <div className="flex flex-col gap-0">
                    {/* Member list */}
                    <div className="flex flex-col" style={{ maxHeight: 220, overflowY: "auto" }}>
                      {shareOwnerId != null ? (
                        <div
                          className="flex items-center justify-between gap-3 py-2.5"
                          style={{ borderBottom: "1px solid var(--border-default)" }}
                        >
                          <div className="min-w-0 flex-1">
                            {(() => {
                              const om = teamMembers.find((m) => getTeamMemberUserIdInt(m) === shareOwnerId);
                              return (
                                <>
                                  <div className="text-[13px] font-medium text-[color:var(--text-primary)]">{om?.name || "Owner"}</div>
                                  <div className="text-[11px] text-[color:var(--text-muted)]">{om?.email || `User #${shareOwnerId}`}</div>
                                </>
                              );
                            })()}
                          </div>
                          <span className="shrink-0 rounded-md border border-[color:var(--border-default)] px-2.5 py-1 text-[11px] font-medium text-[color:var(--text-muted)]">Owner</span>
                        </div>
                      ) : null}

                      {grantedUserIdsSorted.map((uidInt) => {
                        const idStr = String(uidInt);
                        const role = grantedAccess[idStr] === "editor" ? "editor" : "viewer";
                        const mem = teamMembers.find((m) => getTeamMemberUserIdInt(m) === uidInt);
                        return (
                          <div
                            key={`granted-${idStr}`}
                            className="flex items-center justify-between gap-3 py-2.5"
                            style={{ borderBottom: "1px solid var(--border-default)" }}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="text-[13px] font-medium text-[color:var(--text-primary)]">{mem?.name || `User #${uidInt}`}</div>
                              <div className="text-[11px] text-[color:var(--text-muted)]">{mem?.email || ""}</div>
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                              <select
                                value={role}
                                onChange={(e) => handleGrantedRoleChange(idStr, e.target.value)}
                                className="h-8 rounded-md border border-[color:var(--border-default)] bg-surface px-2 text-[12px] text-[color:var(--text-primary)] outline-none transition-colors hover:bg-surface-hover"
                              >
                                {GRANTED_ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                              </select>
                              <button
                                type="button"
                                onClick={() => handleRemoveGranted(idStr)}
                                className="text-[12px] text-[color:var(--feedback-error)] transition-colors hover:underline"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Add member row */}
                    <div
                      className="flex flex-wrap items-center gap-2 pt-3"
                      style={{ borderTop: "1px solid var(--border-default)" }}
                    >
                      <div className="w-full text-[12px] text-[color:var(--text-muted)]">Add a team member</div>
                      <MemberPicker
                        options={shareAddMemberOptions}
                        value={shareAddUserId}
                        onChange={setShareAddUserId}
                        disabled={shareLoading || shareAddMemberOptions.length === 0}
                      />
                      <select
                        value={shareAddRole}
                        onChange={(e) => setShareAddRole(e.target.value)}
                        className="h-8 w-[130px] shrink-0 rounded-md border border-[color:var(--border-default)] bg-surface px-2 text-[12px] text-[color:var(--text-primary)] outline-none transition-colors hover:bg-surface-hover"
                      >
                        {GRANTED_ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      <button
                        type="button"
                        onClick={handleAddGrantedMember}
                        disabled={!shareAddUserId}
                        className="h-8 rounded-md border border-[color:var(--border-default)] bg-surface px-3 text-[12px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover disabled:opacity-40"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-[13px] text-[color:var(--text-muted)]">Only the board owner can grant or change team access.</p>
                )}
              </div>

              {/* Divider */}
              <div className="border-t border-[color:var(--border-default)]" />

              {/* ── Public link ── */}
              <div>
                <div className="mb-2 text-[13px] font-semibold text-[color:var(--text-primary)]">Public link</div>
                {shareLoading && !publicUrl ? (
                  <p className="text-[12px] text-[color:var(--text-muted)]">Loading…</p>
                ) : null}
                {publicUrl ? (
                  <div
                    className="flex items-center gap-2 rounded-md p-3"
                    style={{ backgroundColor: "var(--surface-sunken)", border: "1px solid var(--border-default)" }}
                  >
                    <input
                      value={publicUrl}
                      readOnly
                      className="min-w-0 flex-1 bg-transparent text-[12px] text-[color:var(--text-primary)] outline-none"
                      style={{ fontFamily: "Inter, sans-serif" }}
                    />
                    <button
                      type="button"
                      onClick={copyPublicUrl}
                      className="h-8 shrink-0 rounded-md border px-3 text-[12px] font-medium transition-colors"
                      style={{
                        borderColor: urlCopied ? "var(--feedback-success)" : "var(--brand-primary)",
                        color: urlCopied ? "var(--feedback-success)" : "var(--brand-primary)",
                        backgroundColor: "var(--surface-base)",
                      }}
                    >
                      {urlCopied ? "Copied!" : "Copy"}
                    </button>
                  </div>
                ) : null}
                {!publicUrl && !shareLoading && canManagePublicLink ? (
                  <button
                    type="button"
                    onClick={handleCreatePublicLink}
                    disabled={shareLoading}
                    className="h-8 rounded-md border border-[color:var(--border-default)] bg-surface px-3 text-[12px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover disabled:opacity-60"
                  >
                    {shareLoading ? "Creating…" : "Create public link"}
                  </button>
                ) : null}
                {!publicUrl && !shareLoading && !canManagePublicLink ? (
                  <p className="text-[13px] text-[color:var(--text-muted)]">No public link yet. Ask someone with edit access to create one.</p>
                ) : null}
              </div>
            </div>

            {/* Footer */}
            {(isBoardOwnerUser && permissionsDirty) || (publicUrl && canManagePublicLink) ? (
              <div className="flex shrink-0 items-center justify-end gap-2 border-t border-[color:var(--border-default)] px-6 py-3">
                {publicUrl && canManagePublicLink ? (
                  <button
                    type="button"
                    onClick={revokeBoardShare}
                    disabled={shareLoading}
                    className="h-8 rounded-md border border-[color:var(--feedback-error)] px-3 text-[12px] font-medium text-[color:var(--feedback-error)] transition-colors hover:bg-[color:color-mix(in_srgb,var(--feedback-error)_6%,transparent)] disabled:opacity-60"
                  >
                    Revoke public link
                  </button>
                ) : null}
                {isBoardOwnerUser && permissionsDirty ? (
                  <button
                    type="button"
                    onClick={handleSaveBoardPermissions}
                    disabled={permissionsSaving}
                    className="h-8 rounded-md px-3 text-[12px] font-semibold text-ink-onBrand transition-colors disabled:opacity-60"
                    style={{ backgroundColor: "var(--brand-primary)" }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "var(--brand-primary-hover)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "var(--brand-primary)"; }}
                  >
                    {permissionsSaving ? "Saving…" : "Save team access"}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── Board Time Button ───────────────────────────────────────────────────────

// ─── Member Picker ─────────────────────────────────────────────────────────────
// Custom searchable dropdown — fully token-driven, works in both light and dark mode.

function MemberPicker({ options = [], value, onChange, disabled = false }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState(null);
  const triggerRef = useRef(null);
  const dropRef = useRef(null);
  const inputRef = useRef(null);

  const selected = options.find((o) => String(o.value) === String(value));

  const filtered = query.trim()
    ? options.filter((o) => (o.label || "").toLowerCase().includes(query.trim().toLowerCase()))
    : options;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        dropRef.current && !dropRef.current.contains(e.target)
      ) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const handleOpen = () => {
    if (disabled || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    // Position below the trigger; flip upward if not enough space below
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropHeight = Math.min(options.length * 32 + 48, 260); // estimate
    const top = spaceBelow > dropHeight || spaceBelow > 100
      ? rect.bottom + 4
      : rect.top - dropHeight - 4;
    setDropPos({ top, left: rect.left, width: rect.width });
    setOpen(true);
    setQuery("");
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleSelect = (opt) => {
    onChange(opt.value);
    setOpen(false);
    setQuery("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
    setQuery("");
  };

  return (
    <div className="relative min-w-[200px] flex-1" style={{ fontFamily: "Inter, sans-serif" }}>
      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        onClick={handleOpen}
        disabled={disabled}
        className={`flex h-8 w-full items-center gap-2 rounded-md border border-[color:var(--border-default)] bg-surface px-3 text-left text-[12px] transition-colors hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-50 ${open ? "border-[color:var(--brand-primary)] ring-1 ring-[color:var(--brand-primary)]" : ""}`}
      >
        <span className={`flex-1 truncate ${selected ? "text-[color:var(--text-primary)]" : "text-[color:var(--text-muted)]"}`}>
          {selected ? selected.label : "Choose someone"}
        </span>
        {selected ? (
          <span
            role="button"
            tabIndex={-1}
            onClick={handleClear}
            className="shrink-0 cursor-pointer text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] transition-colors"
            aria-label="Clear"
          >
            <X size={12} />
          </span>
        ) : (
          <ChevronLeft size={13} className="shrink-0 -rotate-90 text-[color:var(--text-muted)]" />
        )}
      </button>

      {/* Dropdown: portalled to body so it escapes any overflow:hidden container */}
      {open && dropPos && typeof document !== "undefined" && createPortal(
        <div
          ref={dropRef}
          className="fixed overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface shadow-token-lg"
          style={{ top: dropPos.top, left: dropPos.left, width: dropPos.width, zIndex: 100002, fontFamily: "Inter, sans-serif" }}
        >
          {/* Search input */}
          <div className="border-b border-[color:var(--border-default)] px-3 py-2">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search members…"
              className="w-full bg-transparent text-[12px] text-[color:var(--text-primary)] outline-none placeholder:text-[color:var(--text-muted)]"
            />
          </div>
          {/* Options */}
          <ul className="max-h-[200px] overflow-y-auto py-1" role="listbox">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-[12px] text-[color:var(--text-muted)]">No members found</li>
            ) : filtered.map((opt) => {
              const isSelected = String(opt.value) === String(value);
              return (
                <li key={opt.value} role="none">
                  <button
                    type="button"
                    onClick={() => handleSelect(opt)}
                    className={`flex h-8 w-full items-center gap-2 px-3 text-left text-[12px] transition-colors ${
                      isSelected
                        ? "bg-surface-hover font-medium text-[color:var(--text-primary)]"
                        : "text-[color:var(--text-primary)] hover:bg-surface-hover"
                    }`}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <span className="flex-1 truncate">{opt.label}</span>
                    {isSelected && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[color:var(--brand-primary)]" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── Board Time Button ──────────────────────────────────────────────────────────

const BTB_PRESET_INTERVAL = {
  Today: "day", Yesterday: "day",
  "7D": "day", "30D": "day", "3M": "day", "6M": "day", "12M": "day",
};

function btbNormalizeCustomRange(fromIso, toIso, enableTimeRanges) {
  if (enableTimeRanges) return { start: fromIso, end: toIso };
  const toLocalYmd = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  const fromYmd = toLocalYmd(fromIso);
  const toYmd = toLocalYmd(toIso);
  if (!fromYmd || !toYmd) return { start: fromIso, end: toIso };
  const nextDayYmd = (ymd) => {
    const d = new Date(ymd + "T12:00:00.000Z");
    d.setUTCDate(d.getUTCDate() + 1);
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
  };
  return { start: fromYmd + "T00:00:00.000Z", end: nextDayYmd(toYmd) + "T00:00:00.000Z" };
}

function btbPresetToBoardTime(preset) {
  return { mode: "preset", preset, start: null, end: null, compare: null, interval: BTB_PRESET_INTERVAL[preset] || "day", timezone: "UTC" };
}

const BTB_PRESETS = ["7D", "30D", "3M", "6M", "12M"];

function getBtbLabel(boardTime) {
  if (!boardTime || boardTime.mode === "default") return "Default";
  if (boardTime.mode === "preset") return boardTime.preset || "30D";
  if (boardTime.mode === "custom") {
    if (boardTime.start && boardTime.end) {
      const fmt = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
      return `${fmt(boardTime.start)} – ${fmt(boardTime.end)}`;
    }
    return "Custom";
  }
  return "Default";
}

// Step order for the board time stepper — matches EventsTimeFilter's STEP_ORDER pattern
const BTB_STEP_OPTIONS = [
  { value: "default", label: "Default" },
  { value: "7D",      label: "7D" },
  { value: "30D",     label: "30D" },
  { value: "3M",      label: "3M" },
  { value: "6M",      label: "6M" },
  { value: "12M",     label: "12M" },
  { value: "custom",  label: "Custom" },
];

function getBtbStepValue(boardTime) {
  if (!boardTime || boardTime.mode === "default") return "default";
  if (boardTime.mode === "custom") return "custom";
  return boardTime.preset || "default";
}

/**
 * Board time range selector — identical stepper [← label →] shape to EventsTimeFilter.
 * Matches: h-8 rounded-md border, chevron buttons on each side, label in centre.
 */
function BoardTimeButton({ boardTime, onBoardTimeChange }) {
  const [dropOpen, setDropOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customPos, setCustomPos] = useState(null);
  const containerRef = useRef(null);
  const dropRef = useRef(null);
  const customBtnRef = useRef(null);

  const currentValue = getBtbStepValue(boardTime);
  const currentIdx = Math.max(0, BTB_STEP_OPTIONS.findIndex((o) => o.value === currentValue));
  const hasPrev = currentIdx > 0;
  const hasNext = currentIdx < BTB_STEP_OPTIONS.length - 1;

  // Current display label
  const displayLabel = (() => {
    if (boardTime?.mode === "custom" && boardTime.start && boardTime.end) {
      const fmt = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
      return `${fmt(boardTime.start)} – ${fmt(boardTime.end)}`;
    }
    return BTB_STEP_OPTIONS[currentIdx]?.label || "Default";
  })();

  const isActive = currentValue !== "default";

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropOpen) return;
    const onDown = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target) &&
          containerRef.current && !containerRef.current.contains(e.target)) {
        setDropOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [dropOpen]);

  // Close custom popover on outside click
  useEffect(() => {
    if (!customOpen) return;
    const onDown = (e) => {
      if (e.target.closest?.("[data-btb-custom-popover]")) return;
      setCustomOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [customOpen]);

  const applyOption = (value) => {
    if (value === "default") { onBoardTimeChange({ mode: "default" }); setDropOpen(false); return; }
    if (value === "custom") {
      if (customBtnRef.current) {
        const r = customBtnRef.current.getBoundingClientRect();
        setCustomPos({ top: r.bottom + 6, left: Math.max(8, r.left) });
      }
      setDropOpen(false);
      setCustomOpen(true);
      return;
    }
    onBoardTimeChange(btbPresetToBoardTime(value));
    setDropOpen(false);
  };

  const handleCustomApply = (filter) => {
    if (filter.type === "custom" && filter.from && filter.to) {
      const { start, end } = btbNormalizeCustomRange(filter.from, filter.to, filter.enableTimeRanges === true);
      onBoardTimeChange({ mode: "custom", preset: null, start, end, compare: null, interval: "day", timezone: "UTC" });
    }
    setCustomOpen(false);
  };

  return (
    <div className="relative inline-flex h-8 items-stretch" ref={containerRef}>
      {/* Outer pill: identical to EventsTimeFilter */}
      <div className={`inline-flex h-8 items-stretch overflow-hidden rounded-md border transition-colors ${
        isActive
          ? "border-[color:var(--brand-primary)]"
          : "border-[color:var(--border-default)] bg-surface"
      }`}
        style={isActive ? { backgroundColor: "color-mix(in srgb, var(--brand-primary) 8%, transparent)" } : undefined}
      >
        {/* Prev */}
        <button
          type="button"
          onClick={() => hasPrev && applyOption(BTB_STEP_OPTIONS[currentIdx - 1].value)}
          disabled={!hasPrev}
          title="Previous range"
          className={`flex w-7 items-center justify-center transition-colors hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-30 ${
            isActive ? "text-[color:var(--brand-primary)]" : "text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"
          }`}
          aria-label="Previous range"
        >
          <ChevronLeft size={14} />
        </button>

        {/* Label / opener */}
        <button
          type="button"
          onClick={() => setDropOpen((v) => !v)}
          title="Click to change range"
          className={`flex min-w-[100px] items-center justify-center gap-1.5 border-x border-[color:var(--border-default)] px-3 text-[12px] font-medium transition-colors hover:bg-surface-hover ${
            isActive ? "text-[color:var(--brand-primary)]" : "text-[color:var(--text-primary)]"
          }`}
          style={{ fontFamily: "Inter, sans-serif" }}
          aria-haspopup="listbox"
          aria-expanded={dropOpen}
        >
          <Clock size={13} className={isActive ? "text-[color:var(--brand-primary)]" : "text-[color:var(--text-muted)]"} />
          <span className="max-w-[100px] truncate whitespace-nowrap">{displayLabel}</span>
        </button>

        {/* Next */}
        <button
          type="button"
          onClick={() => hasNext && applyOption(BTB_STEP_OPTIONS[currentIdx + 1].value)}
          disabled={!hasNext}
          title="Next range"
          className={`flex w-7 items-center justify-center transition-colors hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-30 ${
            isActive ? "text-[color:var(--brand-primary)]" : "text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"
          }`}
          aria-label="Next range"
        >
          <ChevronRight size={14} />
        </button>
      </div>

      {/* Dropdown list: same style as EventsTimeFilter dropdown */}
      {dropOpen && (
        <div
          ref={dropRef}
          className="absolute left-0 top-full z-50 mt-1 w-[160px] overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface shadow-token-lg"
          role="listbox"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          {BTB_STEP_OPTIONS.map((opt) => {
            const active = currentValue === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                ref={opt.value === "custom" ? customBtnRef : undefined}
                onClick={() => applyOption(opt.value)}
                className={`flex h-8 w-full items-center gap-2 px-3 text-left text-[12px] transition-colors ${
                  active
                    ? "bg-surface-hover font-medium text-[color:var(--text-primary)]"
                    : "text-[color:var(--text-primary)] hover:bg-surface-hover"
                }`}
                role="option"
                aria-selected={active}
              >
                <span className="flex-1">{opt.label}</span>
                {active && <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--brand-primary)]" />}
              </button>
            );
          })}
        </div>
      )}

      {/* Custom date picker popover */}
      {customOpen && customPos && typeof document !== "undefined" && createPortal(
        <div
          data-btb-custom-popover
          className="fixed z-[100000]"
          style={{ left: customPos.left, top: customPos.top, fontFamily: "Inter, sans-serif" }}
        >
          <EventsTimeFilter
            openCustomOnMount
            hidePresetButtons
            value={boardTime?.mode === "custom" ? { type: "custom", from: boardTime.start, to: boardTime.end } : null}
            onTimeFilterChange={handleCustomApply}
            onClose={() => setCustomOpen(false)}
          />
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── Board Icon Picker ────────────────────────────────────────────────────────

const BOARD_ICONS = [
  "📊", "📈", "📉", "📋", "🗃️", "📌",
  "💼", "🏢", "💡", "🎯", "🔍", "⚙️",
  "👥", "🤝", "🏆", "🌟", "🔥", "💎",
  "🚀", "✅", "🎉", "⭐", "💰", "🎨",
  "📅", "⏱️", "🌿", "🌊", "🗺️", "🧩",
  "📝", "🔗", "🔔", "🌐", "📡", "🧪",
  "📦", "🛠️", "🎓", "🏅", "💻", "🔒",
];

function BoardIconPicker({ icon, onIconChange, readOnly, compact = false }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const pickerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (
        pickerRef.current && !pickerRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const handleOpen = () => {
    if (readOnly || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const left = Math.min(rect.left, window.innerWidth - 264);
    setPos({ top: rect.bottom + 6, left: Math.max(8, left) });
    setOpen(true);
  };

  const size = compact ? 28 : 44;
  const fontSize = compact ? 16 : 24;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={handleOpen}
        disabled={readOnly}
        className={`flex-shrink-0 flex items-center justify-center leading-none transition-colors select-none ${compact ? "rounded-md hover:bg-surface-hover" : "rounded-xl"}`}
        style={{
          width: size,
          height: size,
          background: (compact || icon) ? "transparent" : "rgba(0,0,0,0.04)",
          border: `1px solid ${(compact || icon) ? "transparent" : "rgba(0,0,0,0.08)"}`,
          cursor: readOnly ? "default" : "pointer",
          fontSize,
        }}
        title={readOnly ? undefined : "Change board icon"}
      >
        {icon || "📋"}
      </button>

      {open && pos && typeof document !== "undefined" && createPortal(
        <div
          ref={pickerRef}
          className="fixed rounded-2xl border shadow-2xl p-3"
          style={{
            top: pos.top,
            left: pos.left,
            width: 256,
            backgroundColor: "var(--surface-base)",
            borderColor: "rgba(0,0,0,0.1)",
            zIndex: 99999,
            fontFamily: "Inter, sans-serif",
          }}
        >
          <div
            className="text-[11px] font-semibold uppercase tracking-widest mb-2.5 px-0.5"
            style={{ color: baseTheme.textMuted }}
          >
            Board icon
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {BOARD_ICONS.map((ic) => (
              <button
                key={ic}
                type="button"
                onClick={() => { onIconChange(ic); setOpen(false); }}
                className="flex items-center justify-center rounded-lg text-xl leading-none transition-all hover:scale-110 active:scale-95"
                style={{
                  width: 32,
                  height: 32,
                  backgroundColor: ic === icon ? `${baseTheme.primaryLight}18` : "transparent",
                  outline: ic === icon ? `2px solid ${baseTheme.primaryLight}` : "none",
                  outlineOffset: 1,
                }}
                title={ic}
              >
                {ic}
              </button>
            ))}
          </div>
          {icon ? (
            <button
              type="button"
              onClick={() => { onIconChange(null); setOpen(false); }}
              className="mt-2.5 w-full text-xs py-1.5 rounded-lg transition-colors hover:bg-surface-inverse/5"
              style={{ color: baseTheme.textMuted, fontFamily: "Inter, sans-serif" }}
            >
              Remove icon
            </button>
          ) : null}
        </div>,
        document.body
      )}
    </>
  );
}
